<template>
  <component
    :is="tag"
    class="play-badge"
    :class="[
      `play-badge--${variant}`,
      `play-badge--${size}`,
      { 'play-badge--soft': soft, 'play-badge--pill': pill },
    ]"
    v-bind="$attrs"
    v-on="$listeners"
  >
    <!-- VUE3-MIGRATION: $listeners removed in Vue 3 (merged into $attrs).
       On migration delete `v-on="$listeners"` — v-bind="$attrs" forwards listeners too. -->
    <span
      v-if="$slots.icon"
      class="play-badge__icon"
    ><slot name="icon" /></span>
    <slot />
  </component>
</template>

<script>
/**
 * PlayBadge — small inline status/label pill for the Play system.
 *
 * Token-driven (see src/styles/tokens.scss): no hardcoded colors. Replaces
 * bootstrap-vue's <b-badge>. Two fills:
 *   solid (default) — role color bg + role-on text
 *   soft (`soft`)   — role-soft bg + role-strong text (the old `light-*` look)
 *
 * `pill` makes it fully rounded; otherwise a small radius. Renders as <span>
 * by default; use `tag` for other elements (e.g. router-link for filter chips).
 *
 * VUE3-MIGRATION: `v-on="$listeners"` is the only Vue 2-ism here. In Vue 3
 * $listeners is removed (merged into $attrs); drop that line and v-bind="$attrs"
 * keeps forwarding listeners. The props/slots contract is already Vue 3-safe.
 */
export default {
  name: 'PlayBadge',
  inheritAttrs: false,
  props: {
    variant: {
      type: String,
      default: 'primary',
      validator: v => ['primary', 'secondary', 'highlight', 'accent', 'danger', 'ink', 'neutral'].includes(v),
    },
    size: {
      type: String,
      default: 'md',
      validator: v => ['sm', 'md'].includes(v),
    },
    /** Soft tint instead of solid fill */
    soft: {
      type: Boolean,
      default: false,
    },
    /** Fully rounded vs small radius */
    pill: {
      type: Boolean,
      default: false,
    },
    tag: {
      type: String,
      default: 'span',
    },
  },
}
</script>

<style lang="scss" scoped>
.play-badge {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-family: var(--font-display);
  font-weight: 700;
  line-height: 1;
  letter-spacing: -.01em;
  white-space: nowrap;
  border-radius: var(--radius-sm);
  text-decoration: none;

  &__icon {
    display: inline-flex;
    align-items: center;
    font-size: 1.05em;
  }

  &--pill { border-radius: var(--radius-pill); }

  /* ── Sizes ── */
  &--sm { padding: 3px 8px;  font-size: 11px; }
  &--md { padding: 4px 10px; font-size: 12.5px; }

  /* ── Solid fills ── */
  &--primary   { background: var(--c-primary);   color: var(--c-primary-on); }
  &--secondary { background: var(--c-secondary);  color: var(--c-secondary-on); }
  &--highlight { background: var(--c-highlight);   color: var(--c-highlight-on); }
  &--accent    { background: var(--c-accent);      color: var(--c-accent-on); }
  &--danger    { background: var(--c-danger);      color: var(--c-danger-on); }
  &--ink       { background: var(--surface-ink);   color: var(--text-on-ink); }
  &--neutral   { background: var(--line);          color: var(--text-strong); }

  /* ── Soft fills (role-soft bg + role-strong text) ── */
  &--soft.play-badge--primary   { background: var(--c-primary-soft);   color: var(--c-primary-strong); }
  &--soft.play-badge--secondary { background: var(--c-secondary-soft); color: var(--c-secondary-strong); }
  &--soft.play-badge--highlight { background: var(--c-highlight-soft); color: var(--c-highlight-strong); }
  &--soft.play-badge--accent    { background: var(--c-accent-soft);    color: var(--c-accent-strong); }
  &--soft.play-badge--danger    { background: var(--c-danger-soft);    color: var(--c-danger-strong); }
  &--soft.play-badge--ink       { background: var(--line);             color: var(--text-strong); }
  &--soft.play-badge--neutral   { background: var(--line);             color: var(--text-body); }
}
</style>
